'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { sidebarSessionRetrySelectedEvent } from '#pplx/frontend/apps/ask/events/sidebar/analytics.js';
import { useGetRecentItemsWithActiveStreams } from '#pplx/frontend/apps/ask/hooks/recent-items-query-hooks/useGetRecentItemsWithActiveStreams.js';
import { useSidebarV2 } from '#pplx/frontend/apps/ask/hooks/sidebar-graphql-v2/context.js';
import { useSidebarThreadDeletion } from '#pplx/frontend/apps/ask/hooks/sidebar-thread-actions/useSidebarThreadDeletion.js';
import { usePinnedItemsQuery } from '#pplx/frontend/apps/ask/hooks/threads-hooks/usePinnedItemsQuery.js';
import { AutomationItem } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/AutomationItem.js';
import { Empty } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/Empty.js';
import { visibleGraphQLPins } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/graphQLPinnedRows.js';
import { Item } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/Item.js';
import { ThreadItem } from '#pplx/frontend/apps/ask/layout/sidebar/core/lite/internal/ThreadItem.js';
import { isComputerTaskThreadRoute } from '#pplx/frontend/apps/ask/layout/sidebar/core/shared/constants.js';
import { routes } from '#pplx/frontend/apps/ask/routing/spaRoutes.js';
import { getValidPinnedThreadContextUuid, isValidPinnedThreadItem, } from '#pplx/frontend/apps/ask/thread-list/pinnedThreadItem.js';
import { useThreadPinActions } from '#pplx/frontend/apps/ask/thread-list/useThreadPinAction.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { isToday, isYesterday } from '#pplx/frontend/i18n/datetime.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';
const SESSION_DATE_GROUP_ORDER = ['today', 'yesterday', 'earlier'];
function getSessionDateGroup(updatedAt) {
    if (!updatedAt)
        return 'earlier';
    const date = new Date(updatedAt);
    if (Number.isNaN(date.getTime()))
        return 'earlier';
    if (isToday(date))
        return 'today';
    if (isYesterday(date))
        return 'yesterday';
    return 'earlier';
}
export const SessionItems = ({ isSidebarOpen, isInPopover, collapsed, maxItems, foreground, enablePinning = false, groupedHistoryEnabled = false, filter = 'all', }) => {
    const { $t } = useIntl();
    const sidebarV2 = useSidebarV2();
    const { data: sessions, isLoading } = useGetRecentItemsWithActiveStreams({
        reason: 'sidebar-v3',
        graphQLSource: sidebarV2?.recents,
        filter,
        excludeAsi: false,
        reportGraphQLTelemetry: true,
    });
    const { data: pinnedItems = EMPTY_ARRAY } = usePinnedItemsQuery({
        enabled: enablePinning && sidebarV2 == null,
        reason: 'sidebar-history-pin-actions',
    });
    const { deleted, handleDeleteThreadClick } = useSidebarThreadDeletion();
    const { pinThreads: pinSearchThreads, unpinThreads: unpinSearchThreads } = useThreadPinActions({
        surface: 'sidebar',
        threadType: 'search',
    });
    const { pinThreads: pinComputerTasks, unpinThreads: unpinComputerTasks } = useThreadPinActions({
        surface: 'sidebar',
        threadType: 'computer',
    });
    const pinnedContextUuids = useMemo(() => new Set(sidebarV2
        ? (sidebarV2.pins.data?.flatMap((item) => item.sidebarItemType !== 'automation' && item.context_uuid ? [item.context_uuid] : []) ?? [])
        : pinnedItems.flatMap((item) => {
            const contextUuid = getValidPinnedThreadContextUuid(item);
            return contextUuid == null ? [] : [contextUuid];
        })), [pinnedItems, sidebarV2]);
    const pinnedAutomationIds = useMemo(() => new Set(sidebarV2
        ? (sidebarV2.pins.data?.flatMap((item) => item.sidebarItemType === 'automation' ? [item.automationId] : []) ?? [])
        : pinnedItems.flatMap((item) => item.kind === 'automation' ? [item.automation.automation_id] : EMPTY_ARRAY)), [pinnedItems, sidebarV2]);
    const pinLabel = $t({
        defaultMessage: 'Pin',
        description: 'Context menu label to pin a session from sidebar history',
        id: 'b604080670',
    });
    const unpinLabel = $t({
        defaultMessage: 'Unpin',
        description: 'Context menu label to unpin a session from sidebar history',
        id: 'c38436ceb0',
    });
    const pinMenuItems = useCallback((item) => {
        const contextUuid = item.context_uuid;
        if (!enablePinning || item.variant !== 'thread' || !contextUuid) {
            return [];
        }
        const isPinned = sidebarV2 && item.threadIsPinned != null
            ? item.threadIsPinned
            : pinnedContextUuids.has(contextUuid);
        const isComputerTask = isComputerTaskThreadRoute(item.link);
        const pinThreads = isComputerTask ? pinComputerTasks : pinSearchThreads;
        const unpinThreads = isComputerTask ? unpinComputerTasks : unpinSearchThreads;
        return [
            {
                text: isPinned ? unpinLabel : pinLabel,
                icon: isPinned ? $icon('pinned-off') : $icon('pin'),
                onClick: () => isPinned
                    ? unpinThreads([{ contextUUID: contextUuid }])
                    : pinThreads([{ contextUUID: contextUuid }]),
            },
        ];
    }, [
        enablePinning,
        pinComputerTasks,
        pinLabel,
        pinnedContextUuids,
        sidebarV2,
        pinSearchThreads,
        unpinComputerTasks,
        unpinLabel,
        unpinSearchThreads,
    ]);
    const sessionsToShow = useMemo(() => {
        // filter after the live merge so optimistic rows obey the same placement
        // as server rows. Only hide pins that the Pinned section can render.
        const pinnedThreadIds = new Set(enablePinning
            ? sidebarV2
                ? visibleGraphQLPins(sidebarV2.pins.data ?? [], sidebarV2.pins.artifacts ?? EMPTY_ARRAY, deleted, sidebarV2.pins.teammates).flatMap((row) => row.kind === 'session' && row.item.sidebarItemType !== 'automation'
                    ? [row.item.context_uuid ?? row.item.uuid, row.item.uuid]
                    : [])
                : pinnedItems
                    .filter(isValidPinnedThreadItem)
                    .flatMap(({ thread }) => [thread.context_uuid, thread.uuid])
            : []);
        const visibleSessions = sessions.filter((session) => {
            if (deleted.has(session.uuid))
                return false;
            if (session.sidebarItemType === 'automation') {
                return true;
            }
            return (!pinnedThreadIds.has(session.context_uuid ?? session.uuid) &&
                !pinnedThreadIds.has(session.uuid));
        });
        return maxItems == null ? visibleSessions : visibleSessions.slice(0, maxItems);
    }, [deleted, maxItems, sessions, enablePinning, pinnedItems, sidebarV2]);
    const sessionsByDateGroup = {
        today: [],
        yesterday: [],
        earlier: [],
    };
    for (const session of sessionsToShow) {
        sessionsByDateGroup[getSessionDateGroup(session.updated_at)].push(session);
    }
    const groupedSessions = SESSION_DATE_GROUP_ORDER.flatMap((group) => sessionsByDateGroup[group].length > 0 ? [{ group, sessions: sessionsByDateGroup[group] }] : []);
    const dateGroupLabels = {
        today: $t({
            defaultMessage: 'Today',
            description: 'Date group heading for sidebar sessions updated today',
            id: 'f43ecdef06',
        }),
        yesterday: $t({
            defaultMessage: 'Yesterday',
            description: 'Date group heading for sidebar sessions updated yesterday',
            id: '8bcf38a5ff',
        }),
        earlier: $t({
            defaultMessage: 'Earlier',
            description: 'Date group heading for sidebar sessions updated before yesterday',
            id: '5e4794b3be',
        }),
    };
    if (isLoading)
        return null;
    if (!sessionsToShow.length && sidebarV2?.recents.status === 'error') {
        return (_jsx(Button, { variant: "text", size: "small", onClick: sidebarV2.retry, track: sidebarSessionRetrySelectedEvent({
                name: 'sidebar session retry selected',
                data: { section: 'recents' },
            }), children: _jsx(FormattedMessage, { defaultMessage: "Retry loading sessions", id: "5dbe5251f3", description: "Reload sidebar recent sessions after a failure" }) }));
    }
    if (!sessionsToShow.length) {
        return (_jsx(Empty, { variant: foreground ? 'computerInbox' : undefined, text: filter === 'automations'
                ? $t({
                    defaultMessage: 'No recent automations',
                    description: 'Empty state message when the user has no recent automations',
                    id: 'b2ca53e3f0',
                })
                : $t({
                    defaultMessage: 'No recent sessions',
                    description: 'Empty state message when the user has no pinned computer tasks',
                    id: '6514621866',
                }) }));
    }
    const renderSession = (item) => {
        if (item.sidebarItemType === 'automation') {
            return (_jsx(AutomationItem, { source: "recent", item: item, isPinned: item.automationIsPinned ?? pinnedAutomationIds.has(item.automationId), isSidebarOpen: isSidebarOpen, isInPopover: isInPopover, collapsed: collapsed, foreground: foreground, groupedHistoryEnabled: groupedHistoryEnabled }, item.uuid));
        }
        return (_jsx(Session, { item: item, onDeleteClick: handleDeleteThreadClick, sidebarOpen: isSidebarOpen, isInPopover: isInPopover, pinMenuItems: pinMenuItems, collapsed: collapsed, foreground: foreground, groupedHistoryEnabled: groupedHistoryEnabled }, item.uuid));
    };
    const groupedItems = groupedSessions.map(({ group, sessions: groupSessions }, groupIndex) => {
        const label = dateGroupLabels[group];
        return (_jsxs("div", { role: "group", "aria-label": label, className: cn('flex flex-col gap-px', groupIndex > 0 && 'mt-2'), children: [_jsx("div", { className: "flex h-7 items-center px-2", children: _jsx(TextPrimitive, { as: "span", size: "xs", color: "ultraLight", className: "pl-0.5", children: label }) }), groupSessions.map(renderSession)] }, group));
    });
    return (_jsxs(_Fragment, { children: [groupedHistoryEnabled ? groupedItems : sessionsToShow.map(renderSession), _jsx(Item, { variant: "secondary", href: routes['/library'].href(), muted: true, text: $t(defineMessage({
                    defaultMessage: 'View all',
                    description: 'Link after sidebar sessions to open the full library',
                    id: '9cee872ca7',
                })), selected: false, sidebarOpen: isSidebarOpen, isInPopover: isInPopover, collapsed: collapsed, foreground: foreground })] }));
};
const Session = ({ item, pinMenuItems, ...props }) => {
    const additionalMenuItems = useMemo(() => pinMenuItems(item), [item, pinMenuItems]);
    return _jsx(ThreadItem, { ...props, item: item, additionalMenuItems: additionalMenuItems });
};
//# sourceMappingURL=SessionItems.js.map